/**

 * 메뉴 하드코딩 설정 파일

 * - API에서 tabs 정보를 보내지 않으므로 프론트에서 관리

 * - 1뎁스 메뉴 아이콘 정보

 * - 권한 기반 탭 설정

 * - 탭 라벨은 i18n 키(labelKey)로 관리 — i18n/locales/{ko,en}.json 의 menuTab.*

 */



// ============================================

// 타입 정의

// ============================================



export interface MenuTab {

  id: number;

  /** i18n 키 (menuTab.*) — 표시 시 locale에 따라 번역 */

  labelKey: string;

  path: string;

  /** 탭을 표시하기 위해 필요한 권한 코드 (OR 조건) */

  requiredAuthorities?: string[];

  /**
   * 탭을 표시하기 위해 필요한 회사 구독 서비스 코드 (OR 조건).
   * 03: 개인성과 / 04: 인사평가 / 05: 업무
   *
   * 백엔드는 메뉴(프로그램) 단위로만 구독을 거른다(menu.selectList).
   * 매니저대시보드처럼 한 메뉴 안에 서로 다른 모듈의 탭이 섞인 화면은
   * 여기서 한 번 더 걸러야 한다 (AS-IS managerDashboardList.jsp L136~137).
   */

  requiredServices?: string[];

}



export interface MenuTabConfig {

  tabs: MenuTab[];

}



/** 메뉴 탭 라벨 해석 (labelKey 우선, API tabs fallback) */

export function resolveMenuTabLabel(

  tab: { labelKey?: string; label?: string },

  t: (key: string) => string,

): string {

  if (tab.labelKey) {

    return t(tab.labelKey);

  }

  return tab.label || '';

}



// ============================================

// 탭 설정 (path 기준)

// ============================================

const HR_EVAL_TOTAL_CALCULATION_TAB_CONFIG: MenuTabConfig = {
  tabs: [
    { id: 0, labelKey: 'menuTab.hrEval.otherItems', path: '/hr-evaluation/calculation/total/other-items' },
    { id: 1, labelKey: 'menuTab.hrEval.totalCalc', path: '/hr-evaluation/calculation/total/total-calculation' },
    { id: 2, labelKey: 'menuTab.hrEval.totalResults', path: '/hr-evaluation/calculation/total/total-results' },
  ],
};

export function normalizeMenuRoutePath(path: string): string {
  if (!path) return '';
  const trimmed = path.split('?')[0].replace(/\/+$/, '') || '/';
  return trimmed.replace(/^\/screens(?=\/)/, '') || trimmed;
}

export const MENU_TABS_CONFIG: Record<string, MenuTabConfig> = {

  // 대시보드 > 매니저대시보드

  '/dashboard/manager': {

    tabs: [

      // 구독 조건은 AS-IS managerDashboardList.jsp L136~141 과 동일하다.
      // 업무관리=avtServiceYn(05) / 개인성과=perfServiceYn(03)||evalServiceYn(04) / 인사평가=evalServiceYn(04)
      // Timeline 은 AS-IS 에 구독 조건이 없고 권한만 본다.

      {

        id: 0,

        labelKey: 'menuTab.dashboard.taskMgmt',

        path: '/dashboard/manager',

        requiredServices: ['05'],

      },

      {

        id: 1,

        labelKey: 'menuTab.dashboard.indivPerf',

        path: '/dashboard/manager',

        requiredServices: ['03', '04'],

      },

      {

        id: 2,

        labelKey: 'menuTab.dashboard.hrEval',

        path: '/dashboard/manager',

        requiredAuthorities: ['01', '03', '12'],

        requiredServices: ['04'],

      },

      {

        id: 3,

        labelKey: 'menuTab.timeline',

        path: '/dashboard/manager',

        requiredAuthorities: ['01', '03', '12'],

      },

    ],

  },



  // 대시보드 > 개인대시보드

  '/dashboard/personal': {

    tabs: [

      {

        id: 0,

        labelKey: 'menuTab.dashboard.taskMgmt',

        path: '/dashboard/personal',

        requiredServices: ['05'],

      },

      {

        id: 1,

        labelKey: 'menuTab.dashboard.indivPerf',

        path: '/dashboard/personal',

        requiredServices: ['03'],

      },

    ],

  },



  // 조직성과 > 진행현황

  '/org-performance/org-kpi-monitoring': {

    tabs: [

      { id: 0, labelKey: 'menuTab.orgPerf.kpiInput', path: '/org-performance/org-kpi-monitoring' },

      { id: 1, labelKey: 'menuTab.orgPerf.kpiActualInput', path: '/org-performance/org-kpi-monitoring' },

    ],

  },



  // 개인성과 > KPI

  '/performance/kpi': {

    tabs: [

      { id: 0, labelKey: 'menuTab.board', path: '/performance/kpi' },

      { id: 1, labelKey: 'menuTab.cascading', path: '/performance/kpi' },

    ],

  },



  // 개인성과 > OKR

  '/performance/okr': {

    tabs: [

      { id: 0, labelKey: 'menuTab.board', path: '/performance/okr' },

      { id: 1, labelKey: 'menuTab.timeline', path: '/performance/okr' },

      { id: 2, labelKey: 'menuTab.cascading', path: '/performance/okr' },

    ],

  },



  // 개인성과 > 진행현황(status)
  '/performance/status': {
    tabs: [
      { id: 0, labelKey: 'menuTab.perfStatus.kpiInput', path: '/performance/status' },
      { id: 1, labelKey: 'menuTab.perfStatus.kpiDetail', path: '/performance/kpiDetailStatus' },
      { id: 2, labelKey: 'menuTab.perfStatus.okrDetail', path: '/performance/okrDetailStatus' },
    ],
  },
  '/performance/kpiDetailStatus': {
    tabs: [
      { id: 0, labelKey: 'menuTab.perfStatus.kpiInput', path: '/performance/status' },
      { id: 1, labelKey: 'menuTab.perfStatus.kpiDetail', path: '/performance/kpiDetailStatus' },
      { id: 2, labelKey: 'menuTab.perfStatus.okrDetail', path: '/performance/okrDetailStatus' },
    ],
  },
  '/performance/okrDetailStatus': {
    tabs: [
      { id: 0, labelKey: 'menuTab.perfStatus.kpiInput', path: '/performance/status' },
      { id: 1, labelKey: 'menuTab.perfStatus.kpiDetail', path: '/performance/kpiDetailStatus' },
      { id: 2, labelKey: 'menuTab.perfStatus.okrDetail', path: '/performance/okrDetailStatus' },
    ],
  },



  // 업무 > 업무/협업

  '/task/home': {

    tabs: [

      { id: 0, labelKey: 'menuTab.home', path: '/task/home' },

      { id: 1, labelKey: 'menuTab.board', path: '/task/collaboration' },

      { id: 2, labelKey: 'menuTab.timeline', path: '/task/collaboration' },

      { id: 3, labelKey: 'menuTab.summary', path: '/task/collaboration' },

    ],

  },



  // 업무 > 일일/주간보고

  '/task/reports/create': {

    tabs: [

      { id: 0, labelKey: 'menuTab.reports.create', path: '/task/reports/create' },

      { id: 1, labelKey: 'menuTab.reports.view', path: '/task/reports/view' },

    ],

  },



  // 인사평가 > 평가하기

  '/hr-evaluation/evaluate/perform': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrEval.performEval', path: '/hr-evaluation/evaluate/perform' },

      { id: 1, labelKey: 'menuTab.hrEval.abilityEval', path: '/hr-evaluation/evaluate/ability' },

    ],

  },



  // 인사평가 > 평가진행현황

  '/hr-evaluation/progress/targets': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrEval.targetProgress', path: '/hr-evaluation/progress/targets' },

      { id: 1, labelKey: 'menuTab.hrEval.evaluatorProgress', path: '/hr-evaluation/progress/evaluators' },

    ],

  },



  // 인사평가 > 평가산출 > 인사평가산출

  '/hr-evaluation/calculation/perform': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrEval.performEval', path: '/hr-evaluation/calculation/perform' },

      { id: 1, labelKey: 'menuTab.hrEval.abilityEval', path: '/hr-evaluation/calculation/ability' },

    ],

  },



  // 인사평가 > 평가설정 > 기본평가항목관리

  '/hr-evaluation/conf/basis/grade-scale': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrEval.gradeScale', path: '/hr-evaluation/conf/basis/grade-scale' },

      { id: 1, labelKey: 'menuTab.hrEval.gradeDistribution', path: '/hr-evaluation/conf/basis/grade-distribution' },

      { id: 2, labelKey: 'menuTab.hrEval.performItems', path: '/hr-evaluation/conf/basis/performance-items' },

      { id: 3, labelKey: 'menuTab.hrEval.competencyItems', path: '/hr-evaluation/conf/basis/competency-items' },

      { id: 4, labelKey: 'menuTab.hrEval.competencyDict', path: '/hr-evaluation/conf/basis/competency-dictionary' },

    ],

  },



  // 인사평가 > 평가산출 > 평가종합산출

  '/hr-evaluation/calculation/total/other-items': HR_EVAL_TOTAL_CALCULATION_TAB_CONFIG,

  '/hr-evaluation/calculation/total/total-calculation': HR_EVAL_TOTAL_CALCULATION_TAB_CONFIG,

  '/hr-evaluation/calculation/total/total-results': HR_EVAL_TOTAL_CALCULATION_TAB_CONFIG,



  // 인사평가 > 평가결과 > 평가결과조회

  '/hr-evaluation/results/results-view/perform': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrEval.performEval', path: '/hr-evaluation/results/results-view/perform' },

      { id: 1, labelKey: 'menuTab.hrEval.abilityEval', path: '/hr-evaluation/results/results-view/ability' },

    ],

  },



  // 설정관리 > 목표/활동설정

  '/common/config/task-config': {

    tabs: [

      { id: 0, labelKey: 'menuTab.config.okr', path: '/common/config/task-config' },

      { id: 1, labelKey: 'menuTab.config.regularTask', path: '/common/config/task-config' },

      { id: 2, labelKey: 'menuTab.config.idp', path: '/common/config/task-config' },

      { id: 3, labelKey: 'menuTab.config.kpi', path: '/common/config/task-config' },

      { id: 4, labelKey: 'menuTab.config.totalAlign', path: '/common/config/task-config' },

    ],

  },



  // 설정관리 > 조직성과설정

  '/common/config/org-performnace-config': {

    tabs: [

      { id: 0, labelKey: 'menuTab.config.orgPerf', path: '/common/config/org-performnace-config' },

      { id: 1, labelKey: 'menuTab.config.evalGroup', path: '/common/config/org-performnace-config' },

      { id: 2, labelKey: 'menuTab.config.perfOrg', path: '/common/config/org-performnace-config' },

    ],

  },



  // 설정관리 > 메뉴및권한 > 메뉴권한관리 (path가 비어있지만 pgmId로 매칭)

  _PGM1726: {

    tabs: [

      { id: 0, labelKey: 'menuTab.config.menuMgmt', path: '' },

      { id: 1, labelKey: 'menuTab.config.authMgmt', path: '' },

    ],

  },



  // 인사관리 > 연봉관리

  '/hr-mgt/salary-mgt': {

    tabs: [

      { id: 0, labelKey: 'menuTab.hrMgt.salary', path: '/hr-mgt/salary-mgt' },

      { id: 1, labelKey: 'menuTab.hrMgt.salaryTotal', path: '/hr-mgt/salary-mgt' },

    ],

  },

};



// ============================================

// 1뎁스 메뉴 아이콘 설정 (pgmId 기준)

// ============================================



export const MENU_ICONS_CONFIG: Record<string, string> = {

  PGM1720: 'dashboard-icon',

  PGM1727: 'domain-icon',

  PGM1745: 'account-group-icon',

  PGM1701: 'account-star-icon',

  PGM1725: 'briefcase-icon',

  PGM1711: 'account-check-icon',

  PGM1767: 'chart-icon',

  PGM1778: 'account-plus-icon',

  PGM1581: 'account-cog-icon',

  PGM1562: 'domain-icon',

  PGM0009: 'cog-icon',

  PGM1765: 'forum-icon',

  PGM1843: 'pa-icon',

  PGM1844: 'mdi:account-circle',

  PGM1847: 'mdi:account-circle',

  PGM1845: 'mdi:account-circle',

  PGM1846: 'mdi:account-circle',

};



// ============================================

// 유틸리티 함수

// ============================================



export function getMenuTabsConfig(pathOrPgmId: string): MenuTabConfig | undefined {

  if (!pathOrPgmId) {

    return undefined;

  }

  const normalized = normalizeMenuRoutePath(pathOrPgmId);

  if (MENU_TABS_CONFIG[normalized]) {

    return MENU_TABS_CONFIG[normalized];

  }

  if (MENU_TABS_CONFIG[pathOrPgmId]) {

    return MENU_TABS_CONFIG[pathOrPgmId];

  }

  const pgmIdKey = `_${pathOrPgmId}`;

  if (MENU_TABS_CONFIG[pgmIdKey]) {

    return MENU_TABS_CONFIG[pgmIdKey];

  }

  for (const config of Object.values(MENU_TABS_CONFIG)) {

    if (config.tabs.some(tab => normalizeMenuRoutePath(tab.path) === normalized)) {

      return config;

    }

  }

  return undefined;

}



export function getMenuIcon(pgmId: string): string {

  return MENU_ICONS_CONFIG[pgmId] || '';

}



export function filterTabsByAuthority(tabs: MenuTab[], userAuthorities: string[]): MenuTab[] {

  return tabs.filter(tab => {

    if (!tab.requiredAuthorities || tab.requiredAuthorities.length === 0) {

      return true;

    }

    return tab.requiredAuthorities.some(auth => userAuthorities.includes(auth));

  });

}



/**
 * 회사 구독 서비스로 탭을 거른다.
 * requiredServices 가 없는 탭은 그대로 통과하므로 기존 화면에는 영향이 없다.
 */

export function filterTabsByService(tabs: MenuTab[], serviceTypes: string[]): MenuTab[] {

  return tabs.filter(tab => {

    if (!tab.requiredServices || tab.requiredServices.length === 0) {

      return true;

    }

    return tab.requiredServices.some(code => serviceTypes.includes(code));

  });

}



export const DEFAULT_VIEW_TYPE = 'A' as const;



export type ViewType = 'A' | 'T';

